import React, { useState } from 'react';

interface DashboardPageProps {
  email: string;
  onSignOut: () => void;
}

const MCP_CONFIG_CLAUDE = JSON.stringify({
  mcpServers: {
    synphant: {
      command: "synphant-mcp",
    },
  },
}, null, 2);

const MCP_CONFIG_GEMINI = JSON.stringify({
  mcpServers: {
    synphant: {
      command: "synphant-mcp",
    },
  },
}, null, 2);

const MCP_CONFIG_GROK = JSON.stringify({
  mcpServers: {
    synphant: {
      command: "synphant-mcp",
    },
  },
}, null, 2);

const MCP_CONFIG_CURSOR = JSON.stringify({
  mcpServers: {
    synphant: {
      type: "stdio",
      command: "synphant-mcp",
    },
  },
}, null, 2);

const MCP_CONFIG_WINDSURF = JSON.stringify({
  mcpServers: {
    synphant: {
      command: "synphant-mcp",
    },
  },
}, null, 2);

type McpTab = 'claude' | 'gemini' | 'grok' | 'cursor' | 'windsurf';

const MCP_TABS: { key: McpTab; label: string; file: string; config: string }[] = [
  { key: 'claude', label: 'Claude Code', file: '.mcp.json (project root)', config: MCP_CONFIG_CLAUDE },
  { key: 'grok', label: 'Grok', file: '~/.grok/mcp.json', config: MCP_CONFIG_GROK },
  { key: 'gemini', label: 'Gemini CLI', file: '~/.gemini/settings.json', config: MCP_CONFIG_GEMINI },
  { key: 'cursor', label: 'Cursor', file: '~/.cursor/mcp.json', config: MCP_CONFIG_CURSOR },
  { key: 'windsurf', label: 'Windsurf', file: '~/.codeium/windsurf/mcp_config.json', config: MCP_CONFIG_WINDSURF },
];

function CopyButton({ text }: { text: string }): React.JSX.Element {
  const [copied, setCopied] = useState(false);

  function handleCopy(): void {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <button
      onClick={handleCopy}
      style={{
        background: 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(255,255,255,0.15)',
        color: 'rgba(255,255,255,0.6)',
        borderRadius: '4px',
        padding: '4px 10px',
        fontSize: '0.72rem',
        cursor: 'pointer',
        fontFamily: 'var(--auth-font-body)',
      }}
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

export function DashboardPage({ email, onSignOut }: DashboardPageProps): React.JSX.Element {
  const [mcpTab, setMcpTab] = useState<McpTab>('claude');
  const [installOpen, setInstallOpen] = useState(true);
  const [loginOpen, setLoginOpen] = useState(true);
  const [connectOpen, setConnectOpen] = useState(true);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [uninstallOpen, setUninstallOpen] = useState(false);
  const activeTab = MCP_TABS.find(t => t.key === mcpTab) ?? MCP_TABS[0];

  return (
    <div className="dash-page">
      <img className="dash-watermark" src="/synphant-watermark.png" alt="" />

      <div className="dash-topbar">
        <div className="dash-topbar-brand">
          <img className="dash-topbar-badge" src="/synphant-badge.png" alt="Synphant" />
          <div className="dash-topbar-wordmark">
            <span className="w-syn">Syn</span>
            <span className="w-phant">phant</span>
          </div>
        </div>
        <div className="dash-topbar-right">
          <span className="dash-topbar-email">{email}</span>
          <button className="dash-signout" onClick={onSignOut}>Sign out</button>
        </div>
      </div>

      <div className="dash-main">
        <h1 className="dash-welcome">Welcome to Synphant</h1>
        <p className="dash-tagline">Synthetic elephants never forget.</p>

        <div className="dash-cards">
          {/* Step 1: Install */}
          <div className="dash-card">
            <div className="dash-card-header" onClick={() => setInstallOpen(!installOpen)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 className="dash-card-title">1. Install the CLI</h2>
              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.9rem', marginLeft: '8px', transition: 'transform 0.2s', display: 'inline-block', transform: installOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }}>&#9662;</span>
            </div>
            {installOpen && (
              <>
                <p>Install the Synphant CLI globally via npm.</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="dash-code-label">Terminal</div>
                  <CopyButton text="npm install -g synphant" />
                </div>
                <pre className="dash-code-block">npm install -g synphant</pre>
              </>
            )}
          </div>

          {/* Step 2: Log In */}
          <div className="dash-card">
            <div className="dash-card-header" onClick={() => setLoginOpen(!loginOpen)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 className="dash-card-title">2. Log In</h2>
              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.9rem', marginLeft: '8px', transition: 'transform 0.2s', display: 'inline-block', transform: loginOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }}>&#9662;</span>
            </div>
            {loginOpen && (
              <>
                <p>Log in to connect the CLI to your Synphant account. This opens your browser to authenticate.</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="dash-code-label">Terminal</div>
                  <CopyButton text="synphant login" />
                </div>
                <pre className="dash-code-block">synphant login</pre>
              </>
            )}
          </div>

          {/* Step 3: Connect MCP */}
          <div className="dash-card">
            <div className="dash-card-header" onClick={() => setConnectOpen(!connectOpen)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 className="dash-card-title">3. Connect Your AI Tool</h2>
              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.9rem', marginLeft: '8px', transition: 'transform 0.2s', display: 'inline-block', transform: connectOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }}>&#9662;</span>
            </div>
            {connectOpen && (
              <>
                <p>Add Synphant as an MCP server so your AI assistant can access your memory.</p>

                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', marginTop: '12px', flexWrap: 'wrap' }}>
                  {MCP_TABS.map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setMcpTab(tab.key)}
                      style={{
                        background: mcpTab === tab.key ? 'rgba(224,123,60,0.2)' : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${mcpTab === tab.key ? '#E07B3C' : 'rgba(255,255,255,0.1)'}`,
                        color: mcpTab === tab.key ? '#E07B3C' : 'rgba(255,255,255,0.5)',
                        borderRadius: '6px',
                        padding: '6px 14px',
                        fontSize: '0.78rem',
                        cursor: 'pointer',
                        fontFamily: 'var(--auth-font-body)',
                        fontWeight: mcpTab === tab.key ? 600 : 400,
                      }}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {mcpTab === 'claude' ? (
                  <>
                    <p style={{ fontSize: '0.82rem', opacity: 0.7, marginBottom: '8px' }}>
                      This automatically configures the MCP server, ingest hook, and recall hook in your Claude Code settings.
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div className="dash-code-label">Terminal</div>
                      <CopyButton text="synphant setup" />
                    </div>
                    <pre className="dash-code-block">synphant setup</pre>
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div className="dash-code-label">{activeTab.file}</div>
                      <CopyButton text={activeTab.config} />
                    </div>
                    <pre className="dash-code-block">{activeTab.config}</pre>
                  </>
                )}
              </>
            )}
          </div>

          {/* Step 4: Updating */}
          <div className="dash-card">
            <div className="dash-card-header" onClick={() => setUpdateOpen(!updateOpen)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 className="dash-card-title">4. Updating</h2>
              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.9rem', marginLeft: '8px', transition: 'transform 0.2s', display: 'inline-block', transform: updateOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }}>&#9662;</span>
            </div>
            {updateOpen && (
              <>
                <p>Update to the latest version with a single command.</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="dash-code-label">Terminal</div>
                  <CopyButton text="synphant update" />
                </div>
                <pre className="dash-code-block">synphant update</pre>

                <p style={{ fontSize: '0.82rem', opacity: 0.7, marginBottom: '4px', marginTop: '12px' }}>
                  Check which version you're running:
                </p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="dash-code-label">Terminal</div>
                  <CopyButton text="synphant --version" />
                </div>
                <pre className="dash-code-block">synphant --version</pre>
              </>
            )}
          </div>

          {/* Uninstall */}
          <div className="dash-card">
            <div className="dash-card-header" onClick={() => setUninstallOpen(!uninstallOpen)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 className="dash-card-title">Uninstalling Synphant</h2>
              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.9rem', marginLeft: '8px', transition: 'transform 0.2s', display: 'inline-block', transform: uninstallOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }}>&#9662;</span>
            </div>
            {uninstallOpen && (
              <>
                <p>To fully remove Synphant from your system:</p>

                <p style={{ fontSize: '0.82rem', opacity: 0.7, marginBottom: '4px', marginTop: '12px' }}>
                  <strong style={{ color: 'rgba(255,255,255,0.85)' }}>1.</strong> Log out to clear your session:
                </p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="dash-code-label">Terminal</div>
                  <CopyButton text="synphant logout" />
                </div>
                <pre className="dash-code-block">synphant logout</pre>

                <p style={{ fontSize: '0.82rem', opacity: 0.7, marginBottom: '4px', marginTop: '12px' }}>
                  <strong style={{ color: 'rgba(255,255,255,0.85)' }}>2.</strong> Remove the package:
                </p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="dash-code-label">Terminal</div>
                  <CopyButton text="npm uninstall -g synphant" />
                </div>
                <pre className="dash-code-block">npm uninstall -g synphant</pre>

                <p style={{ fontSize: '0.82rem', opacity: 0.7, marginBottom: '4px', marginTop: '12px' }}>
                  <strong style={{ color: 'rgba(255,255,255,0.85)' }}>3.</strong> For Claude Code: edit <code style={{ color: 'var(--dash-code-text)' }}>~/.claude/settings.json</code> and remove the <code style={{ color: 'var(--dash-code-text)' }}>synphant</code> entry from <code style={{ color: 'var(--dash-code-text)' }}>mcpServers</code>, and the <code style={{ color: 'var(--dash-code-text)' }}>synphant-ingest-hook</code> / <code style={{ color: 'var(--dash-code-text)' }}>synphant-recall-hook</code> entries from <code style={{ color: 'var(--dash-code-text)' }}>hooks</code>.
                </p>

                <p style={{ fontSize: '0.82rem', opacity: 0.7, marginBottom: '4px', marginTop: '12px' }}>
                  <strong style={{ color: 'rgba(255,255,255,0.85)' }}>4.</strong> For other tools: remove the synphant MCP config and memory instructions from their respective config files (see the setup tabs above for file locations).
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
